import { useMemo } from 'react';
import { useCustom, useApiUrl } from '@refinedev/core';
import { useAllPages } from '@/hooks/use-all-pages';
import { Plus, X, Package, BookOpen, Hash, Megaphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { type SubscriptionPlan, getSubscriptionPlanName } from '@/resources/subscription-plans/types';
import type { PricingTier } from '@/resources/channel-passes/types';

export type InvitationItemTargetType =
  | 'SUBSCRIPTION_PLAN'
  | 'COURSE'
  | 'CHANNEL_PASS'
  | 'BOOK';

export interface InvitationItem {
  targetType: InvitationItemTargetType;
  targetId: number | null; // null — строка ещё не настроена
  pricingTierId: number | null;
}

interface MinimalEntity {
  id: number;
}

interface CourseLike extends MinimalEntity {
  translations?: Record<string, { name?: string | null } | null> | null;
  slug?: string | null;
}

interface ChannelPassLike extends MinimalEntity {
  translations?: Record<string, { name?: string | null } | null> | null;
}

interface BookLike extends MinimalEntity {
  translations?: Record<string, { title?: string | null } | null> | null;
  slug?: string | null;
}

function pickTranslatedName(
  translations: Record<string, { name?: string | null; title?: string | null } | null> | null | undefined,
  fallback: string,
): string {
  if (!translations) { return fallback; }
  const ru = translations['ru'];
  if (ru?.name) { return ru.name; }
  if (ru?.title) { return ru.title; }
  for (const lang of Object.keys(translations)) {
    const t = translations[lang];
    if (t?.name) { return t.name; }
    if (t?.title) { return t.title; }
  }
  return fallback;
}

interface InvitationItemsBuilderProps {
  value: InvitationItem[];
  onChange: (next: InvitationItem[]) => void;
  /** Запретить добавление SUBSCRIPTION_PLAN (если уже выбран и его не должно быть несколько). По умолчанию разрешено несколько. */
  multiplePlansAllowed?: boolean;
  /** Включён ли блок (например, dialog только что открылся). Тогда подгружаются справочники. */
  enabled?: boolean;
}

/**
 * Универсальный блок «Что выдать» — список item'ов (абонемент / курс / канал / книга),
 * каждый со своим селектором. Кнопки в шапке добавляют пустую строку нужного типа.
 *
 * Item с targetId=null считается «незавершённым» — компонент возвращает его наверх,
 * но валидация полноты (все ли строки заполнены) делается родителем.
 */
export const InvitationItemsBuilder = ({
  value,
  onChange,
  multiplePlansAllowed = true,
  enabled = true,
}: InvitationItemsBuilderProps) => {
  const apiUrl = useApiUrl();

  // Справочники: грузим парционно через useAllPages, чтобы не упереться
  // в backend-лимит limit ≤ 100 и не ломать UI при росте каталогов.
  const { data: plans } = useAllPages<SubscriptionPlan>({
    resource: 'subscription-plans',
    filters: [{ field: 'isActive', operator: 'eq', value: true }],
    enabled,
    staleTime: 30_000,
  });

  const { data: courses } = useAllPages<CourseLike>({
    resource: 'courses',
    enabled,
    staleTime: 30_000,
  });

  const { data: channelPasses } = useAllPages<ChannelPassLike>({
    resource: 'channel-passes',
    enabled,
    staleTime: 30_000,
  });

  const { data: books } = useAllPages<BookLike>({
    resource: 'books',
    enabled,
    staleTime: 30_000,
  });

  const addItem = (targetType: InvitationItemTargetType) => {
    onChange([...value, { targetType, targetId: null, pricingTierId: null }]);
  };

  const removeItem = (idx: number) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  const updateItem = (idx: number, patch: Partial<InvitationItem>) => {
    onChange(value.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  const planCount = value.filter((i) => i.targetType === 'SUBSCRIPTION_PLAN').length;

  return (
    <div className="rounded-lg border p-4 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Package className="h-4 w-4 text-muted-foreground" />
        <Label className="text-sm font-medium">Что выдать после регистрации</Label>
        <div className="flex flex-wrap gap-2 ml-auto">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => addItem('SUBSCRIPTION_PLAN')}
            disabled={!multiplePlansAllowed && planCount > 0}
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Абонемент
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => addItem('COURSE')}>
            <Plus className="h-3.5 w-3.5 mr-1" />
            Курс
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => addItem('CHANNEL_PASS')}>
            <Plus className="h-3.5 w-3.5 mr-1" />
            Канал
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => addItem('BOOK')}>
            <Plus className="h-3.5 w-3.5 mr-1" />
            Книга
          </Button>
        </div>
      </div>

      {value.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Доступы не выбраны. Пользователь зарегистрируется без продуктовых доступов
          (только теги, если выбраны ниже).
        </p>
      )}

      <div className="space-y-2">
        {value.map((item, idx) => (
          <InvitationItemRow
            // Стабильный ключ из (targetType, targetId или idx-prefix для пустых строк).
            // index в чистом виде не годится — порядок может меняться при удалении.
            key={`${item.targetType}-${item.targetId ?? `new-${idx}`}`}
            item={item}
            apiUrl={apiUrl}
            plans={plans}
            courses={courses}
            channelPasses={channelPasses}
            books={books}
            onChange={(patch) => updateItem(idx, patch)}
            onRemove={() => removeItem(idx)}
          />
        ))}
      </div>
    </div>
  );
};

interface RowProps {
  item: InvitationItem;
  apiUrl: string;
  plans: SubscriptionPlan[];
  courses: CourseLike[];
  channelPasses: ChannelPassLike[];
  books: BookLike[];
  onChange: (patch: Partial<InvitationItem>) => void;
  onRemove: () => void;
}

const ICONS: Record<InvitationItemTargetType, typeof Package> = {
  SUBSCRIPTION_PLAN: Package,
  COURSE: BookOpen,
  CHANNEL_PASS: Megaphone,
  BOOK: Hash,
};

const TYPE_LABELS: Record<InvitationItemTargetType, string> = {
  SUBSCRIPTION_PLAN: 'Абонемент',
  COURSE: 'Курс',
  CHANNEL_PASS: 'Канал',
  BOOK: 'Книга',
};

const InvitationItemRow = ({
  item,
  apiUrl,
  plans,
  courses,
  channelPasses,
  books,
  onChange,
  onRemove,
}: RowProps) => {
  const Icon = ICONS[item.targetType];

  // Тарифы для текущего item (по targetType + targetId). Загружается лениво
  // через useCustom — на платформе тарифы привязываются к одному объекту.
  // Для SUBSCRIPTION_PLAN передаётся subscriptionPlanId; для остальных —
  // courseId/channelPassId/bookId.
  const tiersQueryEnabled = item.targetId !== null;
  const tiersQueryParams = useMemo(() => {
    if (!item.targetId) { return null; }
    switch (item.targetType) {
      case 'SUBSCRIPTION_PLAN': return { subscriptionPlanId: item.targetId, isActive: true };
      case 'COURSE': return { courseId: item.targetId, isActive: true };
      case 'CHANNEL_PASS': return { channelPassId: item.targetId, isActive: true };
      case 'BOOK': return { bookId: item.targetId, isActive: true };
    }
  }, [item.targetType, item.targetId]);

  const { query: tiersQuery } = useCustom<PricingTier[]>({
    url: `${apiUrl}/pricing-tiers/admin`,
    method: 'get',
    config: { query: tiersQueryParams ?? {} },
    queryOptions: { enabled: tiersQueryEnabled },
  });

  const tiers: PricingTier[] = Array.isArray(tiersQuery.data?.data) ? tiersQuery.data.data : [];

  // При смене targetId тариф сбрасываем — он мог принадлежать другому объекту.
  const handleTargetChange = (value: string) => {
    const targetId = value ? Number(value) : null;
    onChange({ targetId, pricingTierId: null });
  };

  const handleTierChange = (value: string) => {
    onChange({ pricingTierId: value ? Number(value) : null });
  };

  // Опции и плейсхолдер по типу
  let options: { id: number; label: string }[] = [];
  let placeholder = 'Выберите…';
  switch (item.targetType) {
    case 'SUBSCRIPTION_PLAN':
      options = plans.map((p) => ({ id: p.id, label: `${getSubscriptionPlanName(p)} (${p.code})` }));
      placeholder = 'Выберите абонемент';
      break;
    case 'COURSE':
      options = courses.map((c) => ({
        id: c.id,
        label: pickTranslatedName(c.translations ?? null, c.slug ?? `#${c.id}`),
      }));
      placeholder = 'Выберите курс';
      break;
    case 'CHANNEL_PASS':
      options = channelPasses.map((cp) => ({
        id: cp.id,
        label: pickTranslatedName(cp.translations ?? null, `#${cp.id}`),
      }));
      placeholder = 'Выберите канал';
      break;
    case 'BOOK':
      options = books.map((b) => ({
        id: b.id,
        label: pickTranslatedName(b.translations ?? null, b.slug ?? `#${b.id}`),
      }));
      placeholder = 'Выберите книгу';
      break;
  }

  // Для SUBSCRIPTION_PLAN тариф обязателен, если у плана есть активные тарифы.
  // Для прочих типов тариф опционален: при отсутствии берётся durationDays объекта.
  const requireTier = item.targetType === 'SUBSCRIPTION_PLAN' && tiers.length > 0;
  const tierMissing = requireTier && item.pricingTierId === null;

  return (
    <div className="rounded-md border p-3 space-y-2">
      <div className="flex items-center gap-2">
        <Badge variant="secondary" className="gap-1">
          <Icon className="h-3.5 w-3.5" />
          {TYPE_LABELS[item.targetType]}
        </Badge>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="ml-auto h-7 px-2"
          onClick={onRemove}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <Select
        value={item.targetId !== null ? String(item.targetId) : ''}
        onValueChange={handleTargetChange}
      >
        <SelectTrigger>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.id} value={String(o.id)}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {item.targetId !== null && tiers.length > 0 && (
        <div className="space-y-1">
          <Label className="text-xs">
            Тариф {requireTier && <span className="text-destructive">*</span>}
          </Label>
          <Select
            value={item.pricingTierId !== null ? String(item.pricingTierId) : ''}
            onValueChange={handleTierChange}
          >
            <SelectTrigger>
              <SelectValue placeholder={requireTier ? 'Выберите тариф' : 'Без тарифа (длительность по умолчанию)'} />
            </SelectTrigger>
            <SelectContent>
              {tiers.map((tier) => (
                <SelectItem key={tier.id} value={String(tier.id)}>
                  {formatTierLabel(tier)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {tierMissing && (
            <p className="text-xs text-destructive">Выберите тариф абонемента</p>
          )}
        </div>
      )}
    </div>
  );
};

function formatTierLabel(tier: PricingTier): string {
  const ruName = tier.translations?.ru?.name;
  const name = ruName ?? `Тариф #${tier.id}`;
  const basePrice = tier.prices?.[tier.baseCurrency];
  const priceStr =
    basePrice !== undefined
      ? `${basePrice.toLocaleString('ru-RU')} ${tier.baseCurrency}`
      : '';
  const durationStr = tier.durationDays ? `${tier.durationDays} дн.` : 'бессрочно';
  return [name, priceStr, durationStr].filter(Boolean).join(' — ');
}

/**
 * Проверяет, что все items валидны (targetId выбран, обязательный тариф задан).
 * Возвращает массив человекочитаемых ошибок.
 */
export function validateInvitationItems(
  items: InvitationItem[],
  tiersByItem: Map<number, number>,
): string[] {
  const errors: string[] = [];
  for (const [idx, item] of items.entries()) {
    if (item.targetId === null) {
      errors.push(`Строка #${idx + 1}: выберите ${TYPE_LABELS[item.targetType].toLowerCase()}`);
    }
    if (
      item.targetType === 'SUBSCRIPTION_PLAN' &&
      item.targetId !== null &&
      (tiersByItem.get(idx) ?? 0) > 0 &&
      item.pricingTierId === null
    ) {
      errors.push(`Строка #${idx + 1}: выберите тариф абонемента`);
    }
  }
  return errors;
}
